import React, { useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import Container from "@mui/material/Container";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHistory } from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";

import { setLoading } from "../../../redux/publicReducer/actions";
import { giftcardService } from "../../../services";
import axios from "axios";

const enableSL = parseInt(process.env.REACT_APP_ENABLE_SL);
const apiBaseURL = process.env.REACT_APP_API_BASE_URL;
const serverId = process.env.REACT_APP_SERVER_ID;
const siteId = process.env.REACT_APP_SITE_ID;
const storeId = process.env.REACT_APP_STORE_ID;

export const GiftCardAsPayment = ({ setOpen }) => {
  // Only select what we actually need
  const totalPriceItems = useSelector((state) => state.cart.totalPriceItems);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const tips = useSelector((state) => state.cart.tips);
  const promoCode = useSelector((state) => state.cart.promoCode);
  const { orderProcessingDate, orderProcessingType, selectedAddress } = useSelector((state) => state.public);
  const dispatch = useDispatch();
  const history = useHistory();

  // State management
  const [giftCardInfo, setGiftCardInfo] = useState({});
  const [balance, setBalance] = useState(0);
  const [isBalanceChecked, setIsBalanceChecked] = useState(false);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const [cardNumber, setCardNumber] = useState("");

  // Helper functions for calculations
  const getSubtotalPrice = useCallback(() => {
    if (totalPriceItems > 0) {
      return parseFloat(totalPriceItems);
    }

    const subtotalPrice = cartItems.reduce((subtotal, item) => {
      const isNumber = typeof item.DefaultPrice === "number";
      const itemPrice = isNumber
        ? item.DefaultPrice
        : parseFloat(item.DefaultPrice) || 0;
      const quantity = item.quantity || 1;
      return subtotal + itemPrice * quantity;
    }, 0);

    return parseFloat(subtotalPrice);
  }, [totalPriceItems, cartItems]);

  // Function to create order in the system
  const createOrder = useCallback(async () => {
    try {
      const subTotal = getSubtotalPrice();
      const tax = 0; // Gift card payments typically don't include additional tax
      
      let total = parseFloat(tips || 0) + parseFloat(subTotal);
      
      const url = `${apiBaseURL}/postpayment/setOrder/${siteId}?server_id=${serverId}`;
      const d = new Date(orderProcessingDate);
      
      const response = await axios.post(url, {
        cartItems: cartItems,
        totalPriceItems: totalPriceItems,
        total: total,
        subTotal: subTotal,
        tips: tips || 0,
        tax: tax,
        deliveryCharge: orderProcessingType === 2 ? 0 : "0", // Assuming no delivery charge for gift cards
        serviceCharge: "0",
        transactionFee: "0",
        promoCode: promoCode || "",
        pid: "GiftCard",
        order_id: parseInt(sessionStorage.getItem("order_id") || 0),
        orderProcessingType:
          orderProcessingType === 0
            ? "pickup"
            : orderProcessingType === 1
              ? "dine-in"
              : "delivery",
        orderProcessingDate: {
          date: d.getFullYear() + "/" + (d.getMonth() + 1) + "/" + d.getDate(),
          time: d.getHours() + ":" + d.getMinutes(),
        },
        selectedAddress: selectedAddress,
        instructions: sessionStorage.getItem("instructions") || "",
        name: sessionStorage.getItem("name") || "",
        serverName: "eCommerce",
        tableNumber: sessionStorage.getItem("table"),
        paymentType: "Gift Card",
        giftCardNumber: cardNumber
      });

      sessionStorage.setItem("order_id", response.data.order_id);
      sessionStorage.setItem("user_id", response.data.user_id);
      
      return response.data.order_id;
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    }
  }, [getSubtotalPrice, tips, cartItems, totalPriceItems, orderProcessingDate, orderProcessingType, selectedAddress, promoCode, cardNumber]);

  // Function to send order to econnect
  const sendOrder = useCallback(async (orderId) => {
    try {
      const url = `${apiBaseURL}/postpayment/send_econnect/${siteId}/${storeId}/${orderId}?server_id=${serverId}`;
      
      const response = await axios.get(url);
      console.log("Order sent to econnect:", response);
      
      return response.data;
    } catch (error) {
      console.error("Error sending order:", error);
      throw error;
    }
  }, []);

  // Memoized form configuration
  const formConfig = useMemo(() => ({
    defaultValues: { giftCardNumber: "" },
    mode: "onChange"
  }), []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm(formConfig);

  // Memoized validation rules
  const validationRules = useMemo(() => ({
    required: "Gift card number is required",
    validate: {
      notEmpty: (value) => value?.trim().length > 0 || "Gift card number is required",
      minLength: (value) => value?.trim().length >= 6 || "Gift card number must be at least 6 characters"
    }
  }), []);

  const checkGiftCardBalance = useCallback(async (giftCardNumber) => {
    // Prevent double submission
    if (isCheckingBalance) {
      return;
    }
    
    try {
      setIsCheckingBalance(true);
      dispatch(setLoading(true));
      
      // Validate input
      if (!giftCardNumber || giftCardNumber.trim().length === 0) {
        toast.error("Please enter a valid gift card number");
        return;
      }
      
      const response = await giftcardService.checkBalance(giftCardNumber);
      
      // Check if response exists
      if (!response) {
        toast.error("No response received from server");
        console.error("Gift card balance check: No response received");
        return;
      }
      
      if (response.success) {
        // Validate balance is a number
        const balanceAmount = parseFloat(response.balance);
        if (isNaN(balanceAmount)) {
          toast.error("Invalid balance amount received");
          console.error("Gift card balance check: Invalid balance", response.balance);
          return;
        }
        
        setBalance(balanceAmount);
        setGiftCardInfo(response.giftCardInfo || {});
        setCardNumber(giftCardNumber);
        setIsBalanceChecked(true);
        toast.success(`Gift card balance: $${balanceAmount.toFixed(2)}`);
      } else {
        const errorMessage = response.message || "Invalid gift card number";
        toast.error(errorMessage);
        console.error("Gift card balance check failed:", errorMessage, response);
        
        // Reset state on error
        setBalance(0);
        setGiftCardInfo({});
        setCardNumber("");
        setIsBalanceChecked(false);
      }
    } catch (error) {
      console.error("Gift card balance check error:", error);
      
      // Handle specific error types
      if (error.name === 'AbortError') {
        toast.error("Request timed out. Please try again.");
      } else if (error.response) {
        // API responded with error status
        toast.error(`Server error: ${error.response.status}. Please try again.`);
      } else if (error.request) {
        // Network error
        toast.error("Network error. Please check your connection and try again.");
      } else {
        // Other errors
        toast.error("An unexpected error occurred. Please try again.");
      }
      
      // Reset state on error
      setBalance(0);
      setGiftCardInfo({});
      setCardNumber("");
      setIsBalanceChecked(false);
    } finally {
      setIsCheckingBalance(false);
      dispatch(setLoading(false));
    }
  }, [isCheckingBalance, dispatch]);

  const onPayment = useCallback(async () => {
    // Validate balance and total
    if (!isBalanceChecked) {
      toast.error("Please check your gift card balance first");
      return;
    }
    
    if (typeof balance !== 'number' || isNaN(balance)) {
      toast.error("Invalid gift card balance. Please check again.");
      return;
    }
    
    if (typeof totalPriceItems !== 'number' || isNaN(totalPriceItems) || totalPriceItems <= 0) {
      toast.error("Invalid order total. Please refresh and try again.");
      return;
    }
    
    if (balance >= totalPriceItems) {
      try {
        dispatch(setLoading(true));
        
        // Use the gift card
        const useGiftCardResponse = await giftcardService.useGiftCard(cardNumber, totalPriceItems);
        
        if (useGiftCardResponse.success) {
          toast.success("Gift card payment successful!");
          
          try {
            // Create order in the system first
            const orderId = await createOrder();
            
            // Send order to econnect
            await sendOrder(orderId);
            
            // Store payment information
            sessionStorage.setItem('paymentMethod', 'Gift Card');
            sessionStorage.setItem('paymentStatus', 'success');
            sessionStorage.setItem('giftCardNumber', cardNumber);
            sessionStorage.setItem('redeemedAmount', useGiftCardResponse.data.redeemed_amount);
            sessionStorage.setItem('availableBalance', useGiftCardResponse.data.available_balance);
            
            // Clear cart
            localStorage.clear();
            dispatch({ type: "SET_CART", payload: [] });
            
            // Close modal
            setOpen(false);
            
            toast.success("Order placed successfully!");
            
            // Redirect using history.push with proper logic
            setTimeout(() => {
              history.push(enableSL === 1 ? "/welcome" : "/menu");
            }, 3000);
            
          } catch (orderError) {
            console.error("Error processing order:", orderError);
            toast.error("Payment successful but order processing failed. Please contact support.");
          }
          
        } else {
          toast.error(useGiftCardResponse.message || "Failed to process gift card payment");
        }
      } catch (error) {
        console.error("Gift card payment error:", error);
        toast.error("An error occurred while processing your payment. Please try again.");
      } finally {
        dispatch(setLoading(false));
      }
    } else {
      const shortfall = (totalPriceItems - balance).toFixed(2);
      toast.error(`Insufficient gift card balance. You need $${shortfall} more.`);
    }
  }, [isBalanceChecked, balance, totalPriceItems, cardNumber, dispatch, setOpen, history, createOrder, sendOrder]);

  const handleCheckBalance = useCallback((event) => {
    event?.preventDefault();
    
    // Get the current form values
    const formData = new FormData(document.getElementById("giftcardForm"));
    const cardNumber = formData.get("giftCardNumber") || getValues("giftCardNumber");
    
    if (!cardNumber || !cardNumber.toString().trim()) {
      toast.error("Please enter a gift card number");
      return;
    }
    
    checkGiftCardBalance(cardNumber.toString().trim());
  }, [getValues, checkGiftCardBalance]);


  const handleReset = useCallback(() => {
    setIsBalanceChecked(false);
    setBalance(0);
    setGiftCardInfo({});
    setCardNumber("");
    reset({ giftCardNumber: "" });
  }, [reset]);

  // Memoized styles
  const useStyles = useMemo(() => makeStyles((theme) => ({
    paper: {
      marginTop: theme.spacing ? theme.spacing(8) : '64px',
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    form: {
      width: "100%",
      marginTop: theme.spacing ? theme.spacing(3) : '24px',
    },
    submit: {
      margin: theme.spacing ? theme.spacing(3, 0, 2) : '24px 0 16px',
      backgroundColor: process.env.REACT_APP_BUTTON_COLOR || "#EE060A",
      color: process.env.REACT_APP_BUTTON_TEXT || "#FFFFFF",
      "&:hover": {
        backgroundColor: process.env.REACT_APP_BUTTON_COLOR || "#EE060A",
        opacity: 0.8,
      },
    },
  })), []);

  const classes = useStyles();

  // Memoized computed values
  const formattedBalance = useMemo(() => 
    balance.toFixed(2), [balance]);
  
  const formattedTotal = useMemo(() => 
    totalPriceItems.toFixed(2), [totalPriceItems]);
  
  const shortfall = useMemo(() => 
    balance < totalPriceItems ? (totalPriceItems - balance).toFixed(2) : null, 
    [balance, totalPriceItems]);

  const canUseGiftCard = useMemo(() => 
    balance >= totalPriceItems, [balance, totalPriceItems]);

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Typography component="h1" variant="h5" style={{ textAlign: "center" }}>
          Gift Card Payment
        </Typography>
        
        {isBalanceChecked && (
          <Typography variant="h6" color="primary" style={{ textAlign: "center", margin: "16px 0" }}>
            Balance: ${formattedBalance}
          </Typography>
        )}

        <form
          id="giftcardForm"
          className={classes.form}
          noValidate
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                required
                fullWidth
                id="giftCardNumber"
                label="Gift Card Number"
                name="giftCardNumber"
                autoComplete="off"
                {...register("giftCardNumber", validationRules)}
                error={!!errors.giftCardNumber}
                helperText={errors.giftCardNumber?.message}
                disabled={isBalanceChecked}
              />
            </Grid>
            
            {isBalanceChecked && (
              <Grid item xs={12}>
                <Typography variant="body2" style={{ textAlign: "center", margin: "16px 0" }}>
                  Order Total: ${formattedTotal}
                </Typography>
                {!canUseGiftCard && shortfall && (
                  <Typography variant="body2" color="error" style={{ textAlign: "center" }}>
                    Insufficient balance. You need ${shortfall} more.
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
          
          {!isBalanceChecked ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={isCheckingBalance}
              onClick={handleCheckBalance}
            >
              {isCheckingBalance ? "Checking..." : "Check Balance"}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={!canUseGiftCard}
              onClick={onPayment}
            >
              Use Gift Card
            </Button>
          )}
          
          {isBalanceChecked && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleReset}
              style={{ 
                marginTop: "8px",
                backgroundColor: "#6c757d",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#5a6268"
                }
              }}
            >
              Use Different Card
            </Button>
          )}
        </form>
      </div>
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
};
